import { useState, useEffect, useRef } from "react";
import { useContext, memo } from "react";
import Transmitter from "../components/Transmitter";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import Valve from "../components/Valve";
import "./T102RefluxCond.css";
import { WellContext } from "../context/WellContext";
import { ProcessContext } from "../context/ProcessContext";
import PumpFaceplate from "../components/PumpFaceplate";
import AirCooler from "../components/AirCooler";
import AirCoolerFaceplate from "../components/AirCoolerFaceplate";
import { AirCoolerContext } from "../context/AirCoolerContext";
import { PlantStore } from "../engine/PlantStore";
import { usePlantStore } from "../engine/usePlantStore";
import { trackPageVisit } from "../competency/PageTracker";
import TransmitterFaceplate from "../components/TransmitterFaceplate";





function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}




function AirCoolerPage() {
  const [openPanels, setOpenPanels] = useState([]);

  const openPanel = (tag) => {
    setOpenPanels((prev) =>
      prev.includes(tag) ? prev : [...prev, tag]
    );
  };

  const closePanel = (tag) => {
    setOpenPanels((prev) => prev.filter((p) => p !== tag));
  };

  return (
    <div>
      <AirCooler tag="M102A_A" x={945} y={322}/>
      <AirCooler tag="M102A_B" x={988} y={322}/>
      <AirCooler tag="M102B_A" x={945} y={552}/>
      <AirCooler tag="M102B_B" x={988} y={552}/>
      <AirCooler tag="M102C_A" x={1180} y={322}/>
      <AirCooler tag="M102C_B" x={1223} y={322}/>
      <AirCooler tag="M102D_A" x={1180} y={552}/>
      <AirCooler tag="M102D_B" x={1223} y={552}/>
      {openPanels.map((tag, i) => (
        <AirCoolerFaceplate
          key={tag}
          tag={tag}
          close={() => closePanel(tag)}
          defaultPosition={{ x: 300 + i * 40, y: 120 + i * 40 }}
        />
      ))}
    </div>
  );
}




function PFRefluxCond1() {
  // ✅ Use ref to track render count (for debugging only)
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`PFRefluxCond1 Render: ${renderCount.current}`);

  // ✅ Track page visit only once
  useEffect(() => {
    trackPageVisit("PFRefluxCond1");
  }, []);

  const {processData,  setProcessData} = useContext(ProcessContext);
  const { toggleValve, valves } = useContext(WellContext);
  const [hoveredValve, setHoveredValve] = useState(null);

  const DEFAULT_VALVES = {
    FCV_2501: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2502: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2503: { mode: "Manual", opening: 0, sp: "", pv: "" },
  };

  const [valveData, setValveData] = useState(() => {
    const saved = localStorage.getItem("PFRefluxCond1_valveData");
    if (!saved) return DEFAULT_VALVES;
    try {
      const parsed = JSON.parse(saved);
      return {
        FCV_2501: { ...DEFAULT_VALVES.FCV_2501, ...parsed.FCV_2501 },
        FCV_2502: { ...DEFAULT_VALVES.FCV_2502, ...parsed.FCV_2502 },
        FCV_2503: { ...DEFAULT_VALVES.FCV_2503, ...parsed.FCV_2503 },
      };
    } catch {
      return DEFAULT_VALVES;
    }
  });

  /* ✅ Persist */
  useEffect(() => {
    localStorage.setItem("PFRefluxCond1_valveData", JSON.stringify(valveData));
  }, [valveData]);

  const updateValveData = (tag, field, value) => {
    setValveData(prev => ({
      ...prev,
      [tag]: {
        ...(prev[tag] || DEFAULT_VALVES[tag]),
        [field]: field === "opening" ? Number(value) || 0 : value
      }
    }));
  };

  /* ================= PANEL CONTROL ================= */
  const [openPanels, setOpenPanels] = useState([]);
  const openPanel = (tag) => {
    if (!openPanels.includes(tag)) {
      setOpenPanels(prev => [...prev, tag]);
    }
  };
  const closePanel = (tag) => {
    setOpenPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= PUMP PANEL LOGIC =================
  const [openPumpPanels, setOpenPumpPanels] = useState([]);
  const openPumpPanel = (tag) => {
    if (!openPumpPanels.includes(tag)) {
      setOpenPumpPanels(prev => [...prev, tag]);
    }
  };
  const closePumpPanel = (tag) => {
    setOpenPumpPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= AIR COOLER PANEL =================
  const [openCoolerPanels, setOpenCoolerPanels] = useState([]);
  const openCoolerPanel = (tag) => {
    setOpenCoolerPanels((prev) =>
      prev.includes(tag) ? prev : [...prev, tag]
    );
  };
  const closeCoolerPanel = (tag) => {
    setOpenCoolerPanels((prev) => prev.filter((p) => p !== tag));
  };

  const tagStyle = {
    position: "absolute",
    top: -30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "black",
    color: "#00ffcc",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "1px solid #555",
    whiteSpace: "nowrap",
    zIndex: 999
  };

  const renderInput = ({
    name,
    value,
    setter,
    x,
    y,
    w,
    h,
    bg = "#222",
    color = "#222"
  }) => (
    <input
      value={value}
      onChange={(e) => setter(e.target.value)}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        backgroundColor: bg,
        color,
        border: "1px solid #777",
        borderRadius: "4px",
        padding: "1px 2px",
        fontSize: "14px",
        zIndex: 20,
        textAlign: "center"
      }}
    />
  );

  const A102A_Loovers =  processData.A102A_Loovers ?? 100;
  const A102B_Loovers =  processData.A102B_Loovers ?? 100;
  const A102C_Loovers =  processData.A102C_Loovers ?? 100;
  const A102D_Loovers =  processData.A102D_Loovers ?? 80;
  const ATM_Air =  processData.ATM_Air ?? 25;

  const setA102A_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A102A_Loovers: Number(value)
  }));
};

const setA102B_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A102B_Loovers: Number(value)
  }));
};

const setA102C_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A102C_Loovers: Number(value)
  }));
};

const setA102D_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A102D_Loovers: Number(value)
  }));
};

const setATM_Air = (value) => {
  setProcessData(prev => ({
    ...prev,
    ATM_Air: Number(value)
  }));
};


  // ================= RETREIVING OF DATA FROM OTHER PAGES VIA LOCAL STROAGE =================
  const { transmitters } = usePlantStore();
  const FIC_16009 = transmitters.FIC_16009 || 0;
  const PT_16002 = transmitters.PT_16002 || 0;
  const TT_16002 = transmitters.TT_16002 || 0;



  const FIC_2206 =
  Number(
    localStorage.getItem(
      "FIC_2206"
    )
  ) || 0;


  const TT_2206 =
  Number(
    localStorage.getItem(
      "TT_2206"
    )
  ) || 0;


  const PT_2206 =
  Number(
    localStorage.getItem(
      "PT_2206"
    )
  ) || 0;

/* ==========================================
   BACK PRESSURE FROM REFLUX DRUM
========================================== */

const BackPressureFactor =
Number(
  localStorage.getItem(
    "PF_BackPressure"
  )
) || 0;

localStorage.setItem(
  "BackPressureFactor",
  BackPressureFactor
);



  const xv2501Open = !valves?.XV_2501;
  const FIC_2501 = FIC_2206;
  const TT_2501 = TT_2206;
  const PT_2501 = PT_2206 + (BackPressureFactor * 0.40);
  const PT_2502 = (PT_2501 * 0.3) + (BackPressureFactor * 0.60);
  const TT_2504 = xv2501Open ? 27.564 : 22;



  

  // ======================================================
  // PRE FLASH OVERHEAD VAPOR AIR COOLER CONDENSING LOGIC
  // ======================================================
  const { coolerData } = useContext(AirCoolerContext);

  // OUTPUT TEMPERATURE
  const [TT_2502, setTT_2502] = useState(0);

  // ======================================================
  // GET MOTOR DATA
  // ======================================================
  const getMotor = (tag) => {
    return coolerData?.[tag] || { speed: 0, running: false };
  };

  const M102A_A = getMotor("M102A_A");
  const M102A_B = getMotor("M102A_B");
  const M102B_A = getMotor("M102B_A");
  const M102B_B = getMotor("M102B_B");
  const M102C_A = getMotor("M102C_A");
  const M102C_B = getMotor("M102C_B");
  const M102D_A = getMotor("M102D_A");
  const M102D_B = getMotor("M102D_B");

  // ======================================================
  // COOLER SPEED CALCULATION
  // ======================================================
  const getCoolerSpeed = (A, B) => {
    let speed = 0;
    if (A.running && B.running) {
      speed = (A.speed + B.speed) / 2;
    } else if (A.running) {
      speed = A.speed;
    } else if (B.running) {
      speed = B.speed;
    }
    return speed;
  };

  const A101Speed = getCoolerSpeed(M102A_A, M102A_B);
  const A102Speed = getCoolerSpeed(M102B_A, M102B_B);
  const A103Speed = getCoolerSpeed(M102C_A, M102C_B);
  const A104Speed = getCoolerSpeed(M102D_A, M102D_B);

  // ======================================================
  // RUNNING STATUS
  // ======================================================
  const A101_ON = M102A_A.running || M102A_B.running;
  const A102_ON = M102B_A.running || M102B_B.running;
  const A103_ON = M102C_A.running || M102C_B.running;
  const A104_ON = M102D_A.running || M102D_B.running;

  // ======================================================
  // LOUVERS
  // ======================================================
  const safeLouver = (value) => Math.max(0, Math.min(100, Number(value) || 0));

  const L101 = safeLouver(A102A_Loovers);
  const L102 = safeLouver(A102B_Loovers);
  const L103 = safeLouver(A102C_Loovers);
  const L104 = safeLouver(A102D_Loovers);

  // ======================================================
  // ATMOSPHERIC AIR EFFECT
  // ======================================================
  const atmFactor = Math.max(0.35, 1 - ((Number(ATM_Air) - 25) / 50));

  // ======================================================
  // COOLING DUTY
  // ======================================================
  const duty = (running, speed, louver) => {
    if (!running) return 0;
    const motorCapacity = 28;
    const louverFactor = louver / 100;
    return motorCapacity * (speed / 100) * louverFactor * atmFactor;
  };

  const totalCooling =
    duty(A101_ON, A101Speed, L101) +
    duty(A102_ON, A102Speed, L102) +
    duty(A103_ON, A103Speed, L103) +
    duty(A104_ON, A104Speed, L104);

  // ======================================================
  // TT-2502
  // ======================================================
  const newTT2502 = Number(TT_2501) - totalCooling;

  // ✅ FIXED: Use refs to track values and prevent re-renders
  const TT_2502_Ref = useRef(TT_2502);
  const TT_2503_Ref = useRef(0);
  const TT_2505_Ref = useRef(0);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(false);

  // ✅ Update TT_2502 when dependencies change
  useEffect(() => {
    const calculated = Math.max(newTT2502, Number(ATM_Air));
    TT_2502_Ref.current = calculated;
    setTT_2502(calculated);
  }, [TT_2501, ATM_Air, A101Speed, A102Speed, A103Speed, A104Speed, L101, L102, L103, L104]);

  
  /* =========================================================
   E-114 TAGS
========================================================= */

const TT_2503 = processData.TT_2503;
const TT_2505 = processData.TT_2505;


/* =========================================================
   E-114 REFS
========================================================= */

const E114_intervalRef = useRef(null);
const E114_isMounted = useRef(false);

const E114_processRef = useRef({});


/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E114_processRef.current = {

    TT_2502,
    TT_2504

  };

});


/* =========================================================
   E-114 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E114_isMounted.current) return;

  E114_isMounted.current = true;

  console.log("✅ E-114 interval created");

  E114_intervalRef.current = setInterval(() => {

    const {

      TT_2502,
      TT_2504

    } = E114_processRef.current;


    /* =====================================
       INLETS
    ===================================== */

    const stream1 =
      Number(TT_2502) || 0;

    const stream2 =
      Number(TT_2504) || 0;


    let targetTT2503 = stream1;
    let targetTT2505 = stream2;


    /* =====================================
       DETERMINE HOT/COLD STREAM
    ===================================== */

    const hotter =
      Math.max(stream1, stream2);

    const colder =
      Math.min(stream1, stream2);

    const deltaT =
      hotter - colder;


    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    if (deltaT > 0.05) {

      const effectiveness = 0.22;

      const duty = Math.max(
        0.20,
        deltaT * effectiveness
      );

      const secondaryDuty =
        duty * 0.70;

      if (stream1 >= stream2) {

        // Stream 1 hotter

        targetTT2503 =
          stream1 - duty;

        targetTT2505 =
          stream2 + secondaryDuty;

      }

      else {

        // Stream 2 hotter

        targetTT2503 =
          stream1 + secondaryDuty;

        targetTT2505 =
          stream2 - duty;

      }

    }


    /* =====================================
       PHYSICAL LIMITS
    ===================================== */

    if (stream1 >= stream2) {

      targetTT2503 =
        Math.max(
          targetTT2503,
          stream2 + 0.5
        );

      targetTT2505 =
        Math.min(
          targetTT2505,
          stream1 - 0.5
        );

    }

    else {

      targetTT2505 =
        Math.max(
          targetTT2505,
          stream1 + 0.5
        );

      targetTT2503 =
        Math.min(
          targetTT2503,
          stream2 - 0.5
        );

    }


    /* =====================================
       THERMAL LAG
    ===================================== */

    setProcessData(prev => ({

      ...prev,

      TT_2503: Number(

        (

          (prev.TT_2503 ?? stream1)

          +

          (

            targetTT2503 -

            (prev.TT_2503 ?? stream1)

          ) * 0.08

        ).toFixed(2)

      ),

      TT_2505: Number(

        (

          (prev.TT_2505 ?? stream2)

          +

          (

            targetTT2505 -

            (prev.TT_2505 ?? stream2)

          ) * 0.08

        ).toFixed(2)

      )

    }));

  }, 1000);

  return () => {

    console.log("❌ E-114 interval cleared");

    if (E114_intervalRef.current) {

      clearInterval(E114_intervalRef.current);

      E114_intervalRef.current = null;

    }

    E114_isMounted.current = false;

  };

}, []);









localStorage.setItem(
  "TT_2503",
  TT_2503
);

localStorage.setItem(
  "PT_2502",
  PT_2502
);





  return (
    <div className="PFRefluxCond1-container">
      {/* TITLE */}
      <h2 style={{
        position: "absolute",
        top: "0px",
        left: "240px",
        color: "white",
        background: "rgb(30, 5, 250)",
        padding: "2px 243px",
        zIndex: 10,
      }}>
        T-102 OVERHEAD CONDENSER
      </h2>

      {/* =================SHUTDOWN VALVES ================= */}
      <Valve
        className="XV-2501"
        tag="XV_2501"
        x={287}
        y={426}
        isClosed={valves.XV_2501}
        toggle={toggleValve}
      />

      {renderInput({
        name: "A-101A Loovers",
        value: A102A_Loovers,
        setter: setA102A_Loovers,
        x: 490,
        y: 170,
        bg: "#d7f1d7",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "A-101B Loovers",
        value: A102B_Loovers,
        setter: setA102B_Loovers,
        x: 750,
        y: 170,
        bg: "#bde7bd",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "A-101C Loovers",
        value: A102C_Loovers,
        setter: setA102C_Loovers,
        x: 1010,
        y: 170,
        bg: "#abd6ab",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "A-101D Loovers",
        value: A102D_Loovers,
        setter: setA102D_Loovers,
        x: 1250,
        y: 170,
        bg: "#aad6aa",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "ATM Air",
        value: ATM_Air,
        setter: setATM_Air,
        x: 1250,
        y: 70,
        bg: "#6584a1",
        w: 50,
        h: 20,
      })}

      {/* ================= CONTROL VALVE FACEPLATES ================= */}
      {openPanels.map((tag, index) => (
        <ControlValveControlPanel
          key={tag}
          tag={tag}
          data={valveData?.[tag] ?? DEFAULT_VALVES?.[tag]}
          update={updateValveData}
          close={() => closePanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= PUMPS CONTROL VALVE FACEPLATES ================= */}
      {openPumpPanels.map((tag, index) => (
        <PumpFaceplate
          key={tag}
          tag={tag}
          close={() => closePumpPanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= ✅ AIR COOLER FACEPLATES ================= */}
      {openCoolerPanels.map((tag, index) => (
        <AirCoolerFaceplate
          key={tag}
          tag={tag}
          close={() => closeCoolerPanel(tag)}
          defaultPosition={{
            x: 400 + index * 40,
            y: 200 + index * 40
          }}
        />
      ))}

      <Transmitter
        tag="TT_2501"
        value={[TT_2501]}
        x={212}
        y={86}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-101 A-D"
      />

      <Transmitter
        tag="TT_2502"
        value={[TT_2502]}
        x={633}
        y={396}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CONDENSED NAPHTHA & NON CONDENSABLE GASES FROM A-101 A-D TO V-101"
      />

      <Transmitter
        tag="PT_2501"
        value={[PT_2501]}
        x={352}
        y={86}
        unit="MPa"
        max={1}
        HH={0.8}
        H={0.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-101 A-D"
      />

      <Transmitter
        tag="FIC_2501"
        value={[FIC_2501]}
        x={492}
        y={86}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF OVERHEAD VAPOR OF PREFLASH TOWER TO A-101A-D TO A-101 A-D"
      />

      <Transmitter
        tag="TT_2504"
        value={[TT_2504]}
        x={162}
        y={406}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER SUPPLY TO E-114 (PREFLASH OVHD VAPOR CONDENSER)"
      />

      <Transmitter
        tag="TT_2505"
        value={[TT_2505]}
        x={162}
        y={622}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER RETURN OUT OF E-114 TO WATER RETURN MANIFOLD"
      />

      <Transmitter
        tag="TT_2503"
        value={[TT_2503]}
        x={962}
        y={629}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CONDENSED NAPHTHA & NON CONDENSABLE GASES FROM A-101 A-D AND PREFLASH NAPHTHA AFTER COOLER TO V-101"
      />

      <Transmitter
        tag="PT_2502"
        value={[PT_2502]}
        x={792}
        y={629}
        unit="MPa"
        max={1}
        HH={0.8}
        H={0.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-101 A-D"
      />

      <AirCooler
        tag="M102A_A"
        x={366}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M102A_A")}
      />

      <AirCooler
        tag="M102A_B"
        x={411}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M102A_B")}
      />

      <AirCooler
        tag="M102B_A"
        x={639}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M102B_A")}
      />

      <AirCooler
        tag="M102B_B"
        x={684}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M102B_B")}
      />

      <AirCooler
        tag="M102C_A"
        x={895}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M102C_A")}
      />

      <AirCooler
        tag="M102C_B"
        x={942}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M102C_B")}
      />

      <AirCooler
        tag="M102D_A"
        x={1144}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M102D_A")}
      />

      <AirCooler
        tag="M102D_B"
        x={1189}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M102D_B")}
      />
    </div>
  );
}

export default memo(PFRefluxCond1);